import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiPut } from "../api/client";

/** Mirrors LISTING_INTERESTS in backend lib/schemas.py; null = undecided. */
export type Interest = "interested" | "uninterested" | null;

interface HasInterest {
  listing_id: string;
  interest?: Interest;
}

// Writes the mark and patches every cached copy of the listing right away, so
// the card disappears from (or stays in) the filtered list without waiting on
// a refetch of the whole listings table.
export function useSetInterest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ listingId, interest }: { listingId: string; interest: Interest }) =>
      apiPut(`/api/listings/${listingId}/interest`, { interest }),
    onMutate: ({ listingId, interest }) => {
      qc.setQueriesData<{ listings: HasInterest[] }>({ queryKey: ["listings"] }, (data) =>
        data
          ? {
              ...data,
              listings: data.listings.map((l) => (l.listing_id === listingId ? { ...l, interest } : l)),
            }
          : data,
      );
      qc.setQueryData<HasInterest>(["listing", listingId], (data) => (data ? { ...data, interest } : data));
    },
    onSettled: (_data, _err, { listingId }) => {
      void qc.invalidateQueries({ queryKey: ["listings"] });
      void qc.invalidateQueries({ queryKey: ["listing", listingId] });
    },
  });
}

const OPTIONS: { value: "interested" | "uninterested"; icon: string; label: string; color: string }[] = [
  { value: "interested", icon: "👍", label: "Interested", color: "var(--good)" },
  { value: "uninterested", icon: "👎", label: "Not interested", color: "var(--bad)" },
];

/** Thumbs up / down pair. Clicking the active one clears the mark. */
export default function InterestToggle({
  listingId,
  interest,
  compact = false,
}: {
  listingId: string;
  interest: Interest | undefined;
  compact?: boolean;
}) {
  const setInterest = useSetInterest();
  return (
    <div style={{ display: "inline-flex", gap: compact ? 2 : 6 }}>
      {OPTIONS.map((o) => {
        const active = interest === o.value;
        return (
          <button
            key={o.value}
            type="button"
            title={active ? `Marked ${o.label.toLowerCase()} — click to clear` : `Mark ${o.label.toLowerCase()}`}
            aria-pressed={active}
            disabled={setInterest.isPending}
            onClick={(e) => {
              // Cards navigate on click; the toggle shouldn't open the listing.
              e.stopPropagation();
              setInterest.mutate({ listingId, interest: active ? null : o.value });
            }}
            style={{
              background: active ? o.color : "transparent",
              color: active ? "white" : "var(--text)",
              border: `1px solid ${active ? o.color : "var(--border)"}`,
              padding: compact ? "0 5px" : "6px 10px",
              fontSize: compact ? 11 : 13,
              lineHeight: compact ? "18px" : undefined,
              opacity: !compact || active ? 1 : 0.6,
            }}
          >
            {o.icon}
            {!compact && ` ${o.label}`}
          </button>
        );
      })}
    </div>
  );
}
